import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Typography, Avatar, Menu, Divider, ListItemText, ListItemIcon, MenuItem, useTheme, IconButton, Badge } from "@mui/material";
import { useRouter } from "next/router";
import { KeyboardArrowDown, Logout, PersonOutline } from "@mui/icons-material";
import { menuItems } from "@/lib/utils";
import { useAppSelector } from "@/Redux/app/hooks";
import { logoutUser } from "@/Api/ClientHelper";
import { useConfirm } from "material-ui-confirm";
import stringAvatar from "@/lib/stringAvatar";
import { RootState } from "@/Redux/Store";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import KeyboardBackspaceRoundedIcon from "@mui/icons-material/KeyboardBackspaceRounded";
import { getAuthToken } from "@/lib/authCookies";
import { useNotificationSocket } from "@/hooks/useNotificationSocket";
import { usePanelNotificationInbox } from "@/hooks/usePanelNotificationInbox";
import NotificationInboxPanel from "./NotificationInboxPanel";
import { disconnectNotificationSocket } from "@/lib/notificationSocket";
import { PanelInboxNotification } from "@/types/panelNotifications";
interface HeaderProps {
    onMenuClick?: () => void;
    isMobile?: boolean;
}

import CommonIconButton from "../Common/CommonIconButton";

const Header = ({ onMenuClick, isMobile }: HeaderProps) => {
    // ============= Hooks =============
    const theme = useTheme();
    const confirmDialogue = useConfirm();
    const router = useRouter();
    const { id, kitchenId } = router.query;
    const { userDetail } = useAppSelector((state: RootState) => state.auth.loggedInUserDetails);
    const panelNotificationsEnabled =
        userDetail?.userType === "KITCHEN" || userDetail?.userType === "ADMIN";
    const authToken = panelNotificationsEnabled ? (getAuthToken() ?? null) : null;

    // ============= States =============
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [bellAnchorEl, setBellAnchorEl] = useState<null | HTMLElement>(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const inboxOpenRef = useRef(false);
    const fetchInboxRef = useRef<() => Promise<boolean>>(async () => false);

    const {
        items: inboxItems,
        loading: inboxLoading,
        loadingMore: inboxLoadingMore,
        hasMore: inboxHasMore,
        fetchInbox,
        loadMoreInbox,
        removeNotification,
        navigateFromNotification,
    } = usePanelNotificationInbox(userDetail?.userType);

    fetchInboxRef.current = fetchInbox;

    useEffect(() => {
        if (!panelNotificationsEnabled) {
            disconnectNotificationSocket();
            setUnreadCount(0);
        }
    }, [panelNotificationsEnabled]);

    useNotificationSocket(authToken, (count) => {
        setUnreadCount(count);
        if (inboxOpenRef.current) {
            void fetchInboxRef.current();
        }
    });

    const openMenu = Boolean(anchorEl);
    const inboxOpen = Boolean(bellAnchorEl);
    inboxOpenRef.current = inboxOpen;
    const fullName = [userDetail?.firstName, userDetail?.lastName].filter(Boolean).join(" ");

    // ============= Handlers =============
    const getCurrentPageLabel = () => {
        const path = router.pathname;
        if (path === "/edit-profile") return "Edit Profile";
        for (const item of menuItems) {
            const sub = item.subMenu?.find((s) => s.path === path);
            if (sub) return sub.label;
            if (item.path === path) return item.label;
        }
        return "";
    };

    const handleBack = () => { router.back(); };

    const handleLogout = () => {
        setAnchorEl(null);
        confirmDialogue({
            title: "Logout?",
            description: "Are you sure you want to logout?",
            confirmationText: "Logout",
            cancellationText: "Cancel",
            confirmationButtonProps: { color: "error" },
        }).then((res: { confirmed?: boolean }) => {
            if (res?.confirmed) logoutUser();
        });
    };

    const handleBellClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            setAnchorEl(null);
            setBellAnchorEl(event.currentTarget);
            void fetchInbox().then((ok) => {
                if (ok) setUnreadCount(0);
            });
        },
        [fetchInbox]
    );

    const handleInboxClose = () => {
        setBellAnchorEl(null);
    };

    const handleNotificationSelect = (item: PanelInboxNotification) => {
        navigateFromNotification(item);
        handleInboxClose();
    };

    const isDetailPage = !!id;

    const getDetailPageEntity = () => {
        if (router.pathname.includes("/kitchens/")) return "Kitchen";
        return "";
    };

    const profileMenuItemSx = {
        borderRadius: "8px",
        py: 1.25,
        px: 1.5,
        minHeight: 44,
        "& .MuiListItemIcon-root": {
            minWidth: 36,
            color: theme.palette.custom.lightGray,
            "& .MuiSvgIcon-root": { fontSize: 20 },
        },
        "& .MuiListItemText-primary": {
            fontSize: "14px",
            fontWeight: 600,
        },
    };

    return (
        <Box sx={{ height: 80, display: "flex", flexDirection: "column", bgcolor: theme.palette.custom.white, position: "sticky", top: 0, zIndex: 1100 }}>
            <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center", justifyContent: "space-between", px: { xs: 2, sm: 3 } }}>
                <Box display="flex" alignItems="center" gap={2}>
                    {isMobile && (
                        <IconButton onClick={onMenuClick} sx={{ color: theme.palette.custom.black }}>
                            <MenuRoundedIcon />
                        </IconButton>
                    )}
                    {isDetailPage && (
                        <CommonIconButton icon={<KeyboardBackspaceRoundedIcon />} sx={{ color: theme.palette.text.primary }} onClick={handleBack} />
                    )}
                    <Box>
                        {isDetailPage && (
                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: "block", fontSize: "14px", fontWeight: 500, lineHeight: 1.2 }}>
                                {getDetailPageEntity()}
                            </Typography>
                        )}
                        <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.custom.black, fontSize: { xs: "20px", sm: "24px" }, letterSpacing: "-0.5px" }}>
                            {isDetailPage ? (kitchenId) : getCurrentPageLabel()}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1.5}>
                    {panelNotificationsEnabled && (
                        <>
                            <Badge
                                badgeContent={unreadCount}
                                color="primary"
                                invisible={unreadCount === 0}
                                sx={{
                                    "& .MuiBadge-badge": {
                                        right: 4,
                                        top: 4,
                                        color: "white",
                                    },
                                }}
                            >
                                <CommonIconButton
                                    variant="secondary"
                                    src="/images/Bell.svg"
                                    size={20}
                                    onClick={handleBellClick}
                                />
                            </Badge>
                            <NotificationInboxPanel
                                anchorEl={bellAnchorEl}
                                open={inboxOpen}
                                onClose={handleInboxClose}
                                items={inboxItems}
                                loading={inboxLoading}
                                loadingMore={inboxLoadingMore}
                                hasMore={inboxHasMore}
                                onLoadMore={() => void loadMoreInbox()}
                                onSelect={handleNotificationSelect}
                                onDelete={(id) => void removeNotification(id)}
                            />
                        </>
                    )}
                    <Box
                        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
                        sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer", borderRadius: "12px" }}
                    >
                    {userDetail?.profileImage ? <Avatar src={userDetail.profileImage} sx={{ width: 40, height: 40, borderRadius: "10px" }} /> : <Avatar {...stringAvatar(fullName)} />}
                    <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column" }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: 600, color: theme.palette.custom.black, lineHeight: 1.2, textTransform: "capitalize" }}>{fullName}</Typography>
                        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: theme.palette.custom.lightGray, lineHeight: 1.2, textTransform: "capitalize" }}>{userDetail?.userType?.toLowerCase()}</Typography>
                    </Box>
                    <KeyboardArrowDown sx={{ color: theme.palette.custom.lightGray, fontSize: "20px", transform: openMenu ? "rotate(180deg)" : "none", transition: "0.2s" }} />
                    </Box>
                </Box>
            </Box>

            <Box sx={{ mx: 3, height: "1px", bgcolor: theme.palette.divider, opacity: 0.6 }} />

            <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)} transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                slotProps={{ paper: { elevation: 0, sx: { minWidth: 220, borderRadius: "12px", mt: 1.5, border: "1px solid", borderColor: theme.palette.divider, "& .MuiMenu-list": { p: 0 } } } }}>
                <Box sx={{ p: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
                    {userDetail?.profileImage ? <Avatar src={userDetail.profileImage} sx={{ width: 45, height: 45, borderRadius: "10px" }} /> : <Avatar {...stringAvatar(fullName)} />}
                    <Box>
                        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: theme.palette.custom.black, textTransform: "capitalize" }}>{fullName}</Typography>
                        <Typography sx={{ fontSize: "12px", color: theme.palette.custom.lightGray }}>{userDetail?.email}</Typography>
                    </Box>
                </Box>
                <Divider sx={{ borderStyle: "dashed" }} />
                <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                    {userDetail?.userType === "KITCHEN" && (
                        <MenuItem
                            onClick={() => {
                                setAnchorEl(null);
                                router.push("/edit-profile");
                            }}
                            sx={profileMenuItemSx}
                        >
                            <ListItemIcon>
                                <PersonOutline />
                            </ListItemIcon>
                            <ListItemText primary="Edit Profile" />
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={handleLogout}
                        sx={{
                            ...profileMenuItemSx,
                            "&:hover": {
                                bgcolor: theme.palette.error.main + "0A",
                                color: theme.palette.error.main,
                                "& .MuiListItemIcon-root": { color: theme.palette.error.main },
                            },
                        }}
                    >
                        <ListItemIcon>
                            <Logout />
                        </ListItemIcon>
                        <ListItemText primary="Logout" />
                    </MenuItem>
                </Box>
            </Menu>
        </Box>
    );
};

export default Header;
